/**
 * Semantic Lookup Value-Indexing Stream Hook
 *
 * Streams live status for a catalog table's Semantic Lookup indexing job via
 * SSE. Mirrors useProfileSamplesStream exactly - the backend derives progress
 * from the Celery task state plus each column's persisted `value_index_status`.
 */

import { useEffect, useRef, useState } from 'react';
import { AXIOS_INSTANCE } from '../services/api-client';

export type IndexValuesEventType = 'started' | 'progress' | 'heartbeat' | 'completed' | 'failed' | 'timeout';

export interface IndexValuesSummary {
  total: number;
  indexed: number;
  indexing: number;
  stale: number;
  disabled: number;
  index_failed: number;
  not_indexed: number;
  [key: string]: number;
}

export interface IndexValuesEvent {
  event_type: IndexValuesEventType;
  table_id?: number;
  task_id?: string;
  task_state?: string | null;
  summary?: IndexValuesSummary;
  result?: Record<string, unknown>;
  error?: string;
  timestamp: string;
}

export interface IndexValuesStreamState {
  isConnected: boolean;
  isRunning: boolean;
  latest?: IndexValuesEvent;
  summary?: IndexValuesSummary;
  completion?: IndexValuesEvent;
  failure?: IndexValuesEvent;
}

interface UseIndexValuesStreamArgs {
  tableId: number | null;
  taskId: string | null;
  onComplete?: (event: IndexValuesEvent) => void;
  onError?: (event: IndexValuesEvent) => void;
}

const TERMINAL_EVENTS: IndexValuesEventType[] = ['completed', 'failed', 'timeout'];
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_GRACE_MS = 30_000;

export function useIndexValuesStream({
  tableId,
  taskId,
  onComplete,
  onError,
}: UseIndexValuesStreamArgs): IndexValuesStreamState {
  const [state, setState] = useState<IndexValuesStreamState>({
    isConnected: false,
    isRunning: false,
  });
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!tableId || !taskId) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      setState({ isConnected: false, isRunning: false });
      return;
    }

    const apiUrl = (AXIOS_INSTANCE.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(
      /\/$/,
      ''
    );
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.error('No auth token found for value-indexing SSE connection');
      return;
    }

    setState({ isConnected: false, isRunning: true });

    const url =
      `${apiUrl}/api/v1/registry/tables/${tableId}/index-values/stream` +
      `?token=${encodeURIComponent(token)}&task_id=${encodeURIComponent(taskId)}`;
    const eventSource = new EventSource(url, { withCredentials: false });
    eventSourceRef.current = eventSource;
    let reconnectDeadline: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const clearReconnectDeadline = () => {
      if (reconnectDeadline !== null) {
        clearTimeout(reconnectDeadline);
        reconnectDeadline = null;
      }
    };

    const reportTransportFailure = () => {
      if (stopped) return;
      stopped = true;
      clearReconnectDeadline();
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }
      const failureEvent: IndexValuesEvent = {
        event_type: 'failed',
        table_id: tableId,
        task_id: taskId,
        error: 'Lost connection to the indexing stream.',
        timestamp: new Date().toISOString(),
      };
      setState((prev) => ({
        ...prev,
        isConnected: false,
        isRunning: false,
        failure: failureEvent,
      }));
      onErrorRef.current?.(failureEvent);
    };

    eventSource.onopen = () => {
      if (stopped) return;
      clearReconnectDeadline();
      reconnectAttemptsRef.current = 0;
      setState((prev) => ({ ...prev, isConnected: true }));
    };

    eventSource.onmessage = (event) => {
      if (stopped) return;
      let parsed: IndexValuesEvent;
      try {
        parsed = JSON.parse(event.data);
      } catch (error) {
        console.error('Failed to parse value-indexing SSE event:', error);
        return;
      }

      const isTerminal = TERMINAL_EVENTS.includes(parsed.event_type);
      setState((prev) => ({
        isConnected: !isTerminal && prev.isConnected,
        isRunning: !isTerminal,
        latest: parsed,
        summary: parsed.summary ?? prev.summary,
        completion: parsed.event_type === 'completed' ? parsed : prev.completion,
        failure:
          parsed.event_type === 'failed' || parsed.event_type === 'timeout'
            ? parsed
            : prev.failure,
      }));

      if (isTerminal) {
        stopped = true;
        clearReconnectDeadline();
        eventSource.close();
        eventSourceRef.current = null;
        if (parsed.event_type === 'completed') {
          onCompleteRef.current?.(parsed);
        } else {
          onErrorRef.current?.(parsed);
        }
      }
    };

    eventSource.onerror = (error) => {
      if (stopped) return;
      console.error('Value-indexing SSE connection error:', error);
      reconnectAttemptsRef.current += 1;
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        // EventSource automatically reconnects with the same URL/task ID.
        // Keep the operation running while the transport recovers instead of
        // reporting a background task failure after one transient disconnect.
        setState((prev) => ({
          ...prev,
          isConnected: false,
          isRunning: true,
        }));
        if (reconnectDeadline === null) {
          // Some terminal HTTP/proxy failures emit only one error callback.
          // Bound the native EventSource retry window even in that case.
          reconnectDeadline = setTimeout(reportTransportFailure, RECONNECT_GRACE_MS);
        }
        return;
      }

      reportTransportFailure();
    };

    return () => {
      stopped = true;
      clearReconnectDeadline();
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      reconnectAttemptsRef.current = 0;
    };
  }, [tableId, taskId]);

  return state;
}
